// ============================================================
// useProbability - Debounced probability computation hook.
// Recalculates when slot, target, or config changes.
// ============================================================
import { useMemo } from 'react';
import { computeProbabilities } from '../engine/probability';
import { DEFAULT_UNNATURAL_DOS_MODE, type UnnaturalDosMode } from '../engine/unnaturalDos';
import type { ProbabilityInput, ProbabilityResult } from '../engine/probability';
import type { Slot, Target } from '../types';
import type { AttackerCombatStats } from '../engine/character/types';
import type { ConditionModOptions } from '../engine/conditionModifiers';
import { useDebouncedResult } from './useDebouncedResult';

export interface UseProbabilityParams {
  slot: Slot | null;
  target: Target | null;
  attackMod: number;
  rangeBand: string;
  fireMode: string;
  isMelee: boolean;
  hatred: boolean;
  frenzied: boolean;
  attackerCharStats: AttackerCombatStats | null;
  /** Unnatural DoS house rule - forecasts must mirror the live attack math. */
  unnaturalDosMode?: UnnaturalDosMode;
  /** Attacker condition penalties - same reason: the plot must match the roll. */
  attackerConditions?: { conditions: string[] } & Record<string, unknown>;
  applyConditionMods?: boolean;
  /** Which of those the table has switched off. */
  condOptions?: ConditionModOptions;
  /** Aim toggled - Accurate's +10 and bonus dice hinge on it (v8.197.14). */
  aimed?: boolean;
  /** The attack is a charge (v8.368.2). */
  charge?: boolean;
  /** The re-roll damage pill - the roll keeps the better of two damage rolls (v8.390.3). */
  rerollDamage?: boolean;
  /** A chosen hit location ('auto' = rolled): the roll pins every hit there, so the forecast must (v8.390.5). */
  targetLocation?: string;
}

interface UseProbabilityReturn {
  result: ProbabilityResult | null;
  loading: boolean;
}

/**
 * The engine's input for the live attack - shared with the firing
 * solutions board, whose rows must match this forecast and the roll.
 */
export function attackProbabilityInput(p: UseProbabilityParams & { slot: Slot; target: Target }): ProbabilityInput {
  const stats = p.attackerCharStats;
  return {
    slot: p.slot,
    target: p.target,
    attackMod: p.attackMod,
    rangeBand: p.rangeBand,
    fireMode: p.fireMode,
    isMelee: p.isMelee,
    hatred: p.hatred,
    frenzied: p.frenzied,
    damageBonusMelee: stats?.damageBonusMelee ?? 0,
    damageBonusRanged: stats?.damageBonusRanged ?? 0,
    unnaturalDosMode: p.unnaturalDosMode ?? DEFAULT_UNNATURAL_DOS_MODE,
    skillCharValue: stats?.skillCharValue ?? 0,
    skillUnnaturalMultiplier: stats?.skillUnnaturalMultiplier ?? 1,
    attackerConditions: p.attackerConditions,
    applyConditionMods: p.applyConditionMods,
    condOptions: p.condOptions,
    aimed: p.aimed ?? false,
    charge: p.charge ?? false,
    rerollDamage: p.rerollDamage ?? false,
    ...(p.targetLocation && p.targetLocation !== 'auto' ? { forcedLocation: p.targetLocation } : {}),
  };
}

/** Debounced hit / damage / crit forecast for the live attack. Nothing without an armed slot and a target. */
export default function useProbability(params: UseProbabilityParams): UseProbabilityReturn {
  const { slot, target, attackMod, rangeBand, fireMode, isMelee, hatred, frenzied, attackerCharStats } = params;
  const { unnaturalDosMode, attackerConditions, applyConditionMods, condOptions, charge, aimed, rerollDamage } = params;
  const { targetLocation } = params;
  // Long only because the input has this many fields: the memo lists what it reads.
  const input = useMemo(
    () =>
      slot && !slot.isEmpty && target
        ? attackProbabilityInput({
            slot,
            target,
            attackMod,
            rangeBand,
            fireMode,
            isMelee,
            hatred,
            frenzied,
            attackerCharStats,
            unnaturalDosMode,
            attackerConditions,
            applyConditionMods,
            condOptions,
            aimed,
            charge,
            rerollDamage,
            targetLocation,
          })
        : null,
    [
      slot,
      target,
      attackMod,
      rangeBand,
      fireMode,
      isMelee,
      hatred,
      frenzied,
      attackerCharStats,
      unnaturalDosMode,
      attackerConditions,
      applyConditionMods,
      condOptions,
      aimed,
      charge,
      rerollDamage,
      targetLocation,
    ],
  );
  return useDebouncedResult(input, computeProbabilities);
}
